import { useState, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { useI18n } from '../../i18n';
import type { TranslationKey } from '../../i18n/translations';

/* ═══════════════════════════════════════════════════════════════════
   WiFi Provisioning Page — 100% inline styles (no CSS Modules)
   All-in-one file for maximum reliability.
   Supports: Browser (redirect) + Fully Kiosk Browser (openWifiSettings + polling)
   Includes: Diagnostic panel for Fully Kiosk API testing
   Real Fully Kiosk API: openWifiSettings + getWifiSsid + getIp4Address
   ═══════════════════════════════════════════════════════════════════ */

// ── Fully Kiosk Browser API (verified real methods from device) ────
declare global {
  interface Window {
    fully?: {
      // WiFi Info (verified ✅)
      getWifiSsid(): string;
      getIp4Address(): string;
      getIpAddress(): string;
      getIp6Address(): string;
      getWifiSignalLevel(): number;
      getWifiBssid(): string;
      isWifiEnabled(): boolean;
      isWifiConnected(): boolean;
      isNetworkConnected(): boolean;
      // WiFi Control (verified ✅)
      enableWifi(): void;
      disableWifi(): void;
      openWifiSettings(): void;        // ← KEY: opens Android WiFi panel
      // App Launch
      startApplication(packageName: string): void; // Launch installed Android app
      // WiFi Stats
      getAllRxBytesWifi(): number;
      getAllTxBytesWifi(): number;
      // Misc
      getCurrentLocale(): string;
      getFullyVersion(): string;
      getFullyVersionCode(): number;
      getBooleanSetting(key: string): boolean;
      getStringSetting(key: string): string;
      // NOTE: scanWifi / connectWifiSsid / connectWifiSsidPassword / getWifiList
      //       do NOT exist — use openWifiSettings() + polling instead
    };
  }
}

// ── Log buffer (visible on-screen for tablet debugging) ────────────
type LogEntry = { ts: number; level: 'info' | 'ok' | 'warn' | 'err'; msg: string };
const _logs: LogEntry[] = [];
function log(level: LogEntry['level'], msg: string) {
  const entry: LogEntry = { ts: Date.now(), level, msg };
  _logs.push(entry);
  if (_logs.length > 200) _logs.shift();
  const c = level === 'err' ? 'color:#EF4444' : level === 'warn' ? 'color:#F59E0B' : level === 'ok' ? 'color:#10B981' : 'color:#94a3b8';
  console.log(`%c[WiFi] ${msg}`, c);
}

// ── Fully Kiosk Detection & Helpers (real APIs) ───────────────────
function isFullyKiosk(): boolean {
  return typeof window !== 'undefined' && typeof window.fully !== 'undefined';
}

function fullyGetCurrentWifi(): { ssid: string; ip: string } | null {
  if (!isFullyKiosk()) return null;
  try {
    const ssid = window.fully!.getWifiSsid();
    const ip   = window.fully!.getIp4Address() || window.fully!.getIpAddress();
    return ssid ? { ssid, ip } : null;
  } catch (e) { log('err', `fullyGetCurrentWifi: ${e}`); return null; }
}

function fullyOpenWifiSettings(): void {
  if (!isFullyKiosk()) return;
  try {
    log('info', 'Opening Android WiFi settings via openWifiSettings()...');
    window.fully!.openWifiSettings();
  } catch (e) {
    log('err', `fullyOpenWifiSettings: ${e}`);
  }
}
// NOTE: scanWifi / connectWifiSsid / connectWifiSsidPassword / getWifiList
// do NOT exist on this device. Use openWifiSettings() + getWifiSsid() polling.

// ── Types ──────────────────────────────────────────────────────────
interface WifiNetwork {
  ssid: string;
  rssi: number;
  encryption: string;
}
interface StatusResponse {
  connected: boolean;
  ssid: string | null;
  ip: string | null;
  mqtt: boolean;
}
interface ConfigureResponse {
  success: boolean;
  ip?: string;
  error?: string;
}
type Step = 1 | 2 | 3 | 4 | 5;
type SubStep = 'sending' | 'connecting' | 'verifying' | 'done';
type Result = 'success' | 'error' | null;

// ── Diagnostic item type ───────────────────────────────────────────
interface DiagItem {
  label: string;
  fn: () => string;
  status: 'pending' | 'ok' | 'fail';
  value: string;
}

// ── Inline Styles ──────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
  } as CSSProperties,
  header: { textAlign: 'center' as const, marginBottom: 32 } as CSSProperties,
  title: {
    fontSize: 26, fontWeight: 700, margin: '0 0 8px',
    background: 'linear-gradient(135deg, #06B6D4, #10B981)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  } as CSSProperties,
  mockRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginTop: 12, fontSize: 13, color: '#64748b', flexWrap: 'wrap' as const,
  } as CSSProperties,
  stepper: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 0, marginBottom: 36, padding: '0 16px', flexWrap: 'wrap' as const,
  } as CSSProperties,
  stepCircle: {
    width: 36, height: 36, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 13,
    fontWeight: 700, border: '2px solid rgba(255,255,255,0.1)',
    background: '#1e293b', color: '#64748b', flexShrink: 0,
  } as CSSProperties,
  stepActive: { background: 'linear-gradient(135deg,#06B6D4,#10B981)', borderColor: 'transparent', color: '#fff', boxShadow: '0 0 16px rgba(6,182,212,0.3)' } as CSSProperties,
  stepDone: { background: '#10B981', borderColor: '#10B981', color: '#fff' } as CSSProperties,
  stepError: { background: '#EF4444', borderColor: '#EF4444', color: '#fff' } as CSSProperties,
  stepLine: { width: 40, height: 2, background: 'rgba(255,255,255,0.1)', flexShrink: 0 } as CSSProperties,
  stepLineActive: { background: 'linear-gradient(90deg,#06B6D4,#10B981)' } as CSSProperties,
  content: { maxWidth: 680, margin: '0 auto' } as CSSProperties,
  card: {
    background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16, padding: 32,
  } as CSSProperties,
  cardTitle: { fontSize: 20, fontWeight: 700, margin: '0 0 8px' } as CSSProperties,
  cardSub: { color: '#94a3b8', fontSize: 14, margin: '0 0 24px', lineHeight: 1.5 } as CSSProperties,
  btn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: '14px 28px',
    background: 'linear-gradient(135deg,#06B6D4,#10B981)',
    color: '#fff', fontSize: 15, fontWeight: 600, border: 'none',
    borderRadius: 8, cursor: 'pointer', width: '100%',
  } as CSSProperties,
  btnSec: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: '12px 24px', background: 'transparent',
    color: '#94a3b8', fontSize: 14, fontWeight: 500,
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, cursor: 'pointer',
  } as CSSProperties,
  input: {
    width: '100%', padding: '10px 14px', background: '#0f172a',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    color: '#f1f5f9', fontFamily: 'Courier New, monospace', fontSize: 14,
    outline: 'none', boxSizing: 'border-box' as const,
  } as CSSProperties,
  instrItem: {
    display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 16px',
    background: 'rgba(255,255,255,0.03)', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)', marginBottom: 12,
  } as CSSProperties,
  instrNum: {
    width: 32, height: 32, minWidth: 32, borderRadius: '50%',
    background: 'linear-gradient(135deg,#06B6D4,#10B981)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 700,
  } as CSSProperties,
  instrText: { fontSize: 14, color: '#94a3b8', lineHeight: 1.5 } as CSSProperties,
  ssidBadge: {
    display: 'inline-block', background: 'rgba(6,182,212,0.12)', color: '#06B6D4',
    padding: '6px 14px', borderRadius: 20, fontFamily: 'Courier New, monospace',
    fontWeight: 700, fontSize: 15, margin: '4px 0',
  } as CSSProperties,
  netItem: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
    borderRadius: 8, cursor: 'pointer', marginBottom: 8,
    border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)',
  } as CSSProperties,
  netSsid: { fontWeight: 600, fontSize: 15, color: '#f1f5f9' } as CSSProperties,
  netMeta: { fontSize: 12, color: '#64748b', marginTop: 2 } as CSSProperties,
  successIcon: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'rgba(16,185,129,0.15)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
    fontSize: 40, color: '#10B981',
  } as CSSProperties,
  errorIcon: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'rgba(239,68,68,0.15)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
    fontSize: 40, color: '#EF4444',
  } as CSSProperties,
  infoGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '24px 0',
  } as CSSProperties,
  infoItem: {
    padding: 14, background: 'rgba(255,255,255,0.03)',
    borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
  } as CSSProperties,
  infoLabel: { fontSize: 11, color: '#64748b', textTransform: 'uppercase' as const, marginBottom: 4 } as CSSProperties,
  infoVal: { fontSize: 14, fontWeight: 600, color: '#f1f5f9' } as CSSProperties,
};

const alertStyle = (bg: string, c: string): CSSProperties => ({
  padding: '12px 16px', borderRadius: 8, fontSize: 13,
  lineHeight: 1.5, marginBottom: 20, background: bg, color: c,
  border: `1px solid ${c}33`,
});

const spinnerStyle: CSSProperties = {
  width: 40, height: 40, border: '3px solid rgba(6,182,212,0.2)',
  borderTopColor: '#06B6D4', borderRadius: '50%',
  animation: 'wp-spin 0.7s linear infinite', margin: '0 auto 16px',
};
const spinnerSmStyle: CSSProperties = {
  display: 'inline-block', width: 18, height: 18,
  border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#fff',
  borderRadius: '50%', animation: 'wp-spin 0.7s linear infinite',
};

// ── Mock data ──────────────────────────────────────────────────────
const MOCK_NETS: WifiNetwork[] = [
  { ssid: 'Livebox-A1B2', rssi: -42, encryption: 'WPA2' },
  { ssid: 'Freebox-C3D4', rssi: -58, encryption: 'WPA2' },
  { ssid: 'Bbox-E5F6', rssi: -68, encryption: 'WPA2' },
  { ssid: 'SFR_WiFi_G7H8', rssi: -75, encryption: 'WPA' },
  { ssid: 'Hotspot_Voisin', rssi: -85, encryption: 'OPEN' },
];

const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
function isMock(): boolean { return localStorage.getItem('wifi_mock') === 'true'; }

function signalBars(rssi: number): string {
  if (rssi > -50) return '▂▄▆█';
  if (rssi > -65) return '▂▄▆░';
  if (rssi > -80) return '▂▄░░';
  return '▂░░░';
}
function signalLabelKey(rssi: number): TranslationKey {
  if (rssi > -50) return 'pair_signal_excellent';
  if (rssi > -65) return 'pair_signal_good';
  if (rssi > -80) return 'pair_signal_fair';
  return 'pair_signal_weak';
}

// ── Keyframes (injected once) ──────────────────────────────────────
const injectKeyframes = () => {
  if (document.getElementById('wp-kf')) return;
  const el = document.createElement('style');
  el.id = 'wp-kf';
  el.textContent = '@keyframes wp-spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(el);
};

// ── ESP32 HTTP API ─────────────────────────────────────────────────
const ESP = 'http://192.168.4.1';

async function espFetch(url: string, opts: RequestInit = {}, timeout = 5000): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeout);
  try {
    const r = await fetch(url, { ...opts, signal: ctrl.signal, headers: { 'Content-Type': 'application/json' } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r;
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') throw new Error('TIMEOUT');
    if (err instanceof TypeError) throw new Error('NETWORK_ERROR');
    throw err;
  } finally { clearTimeout(t); }
}

let _mC = false, _mIp = '', _mSs = '';

async function apiStatus(): Promise<StatusResponse> {
  if (isMock()) { await wait(600); return _mC ? { connected: true, ssid: _mSs, ip: _mIp, mqtt: true } : { connected: false, ssid: null, ip: null, mqtt: false }; }
  return (await espFetch(`${ESP}/status`)).json();
}
async function apiScan(): Promise<WifiNetwork[]> {
  if (isMock()) { await wait(1500); return MOCK_NETS.map(n => ({ ...n, rssi: n.rssi + Math.floor(Math.random() * 10 - 5) })); }
  const r = await espFetch(`${ESP}/scan`, {}, 10000);
  const d = await r.json(); return d.networks || [];
}
async function apiConfigure(ssid: string, password: string): Promise<ConfigureResponse> {
  if (isMock()) {
    await wait(2000);
    if (password === 'wrong' || password.length < 8) return { success: false, error: 'WRONG_PASSWORD' };
    _mC = true; _mSs = ssid; _mIp = '192.168.1.' + Math.floor(Math.random() * 200 + 10);
    return { success: true, ip: _mIp };
  }
  return (await espFetch(`${ESP}/configure`, { method: 'POST', body: JSON.stringify({ ssid, password }) }, 10000)).json();
}

// ── Diagnostic Panel Component ─────────────────────────────────────
const DiagnosticPanel = ({ onClose }: { onClose: () => void }) => {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<DiagItem[]>([]);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([..._logs]);
  const [running, setRunning] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  const refreshLogs = () => setLogEntries([..._logs]);

  useEffect(() => {
    const iv = setInterval(refreshLogs, 500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logEntries]);

  // State for listing all methods on window.fully
  const [fullyMethods, setFullyMethods] = useState<string[]>([]);
  const [wifiMethods, setWifiMethods] = useState<string[]>([]);

  const runDiag = async () => {
    setRunning(true);
    setFullyMethods([]);
    setWifiMethods([]);

    // ── Step 1: enumerate ALL methods on window.fully ──
    const allMethods: string[] = [];
    const wifiRelated: string[] = [];
    if (window.fully) {
      // Get own properties + prototype chain
      const seen = new Set<string>();
      let obj: object | null = window.fully as object;
      while (obj && obj !== Object.prototype) {
        for (const key of Object.getOwnPropertyNames(obj)) {
          if (!seen.has(key)) {
            seen.add(key);
            allMethods.push(key);
            if (/wifi|network|connect|scan|ssid|ip|signal/i.test(key)) {
              wifiRelated.push(key);
            }
          }
        }
        obj = Object.getPrototypeOf(obj);
      }
      // Also try common bracket access patterns
      const extraChecks = [
        'scanWifi', 'scanWifiNetworks', 'startWifiScan', 'wifiScan',
        'connectWifi', 'connectWifiSsid', 'connectWifiSsidPassword',
        'connectToWifi', 'joinWifi', 'setWifi',
        'getWifiList', 'getWifiNetworks', 'getAvailableWifi',
        'getIpAddress', 'getIp4Address', 'getIp6Address',
        'enableWifi', 'disableWifi', 'getWifiInfo',
      ];
      for (const k of extraChecks) {
        try {
          const v = (window.fully as unknown as Record<string, unknown>)[k];
          if (typeof v !== 'undefined' && !seen.has(k)) {
            seen.add(k);
            allMethods.push(k);
            wifiRelated.push(k);
          }
        } catch { /* ignore */ }
      }
    }
    setFullyMethods(allMethods.sort());
    setWifiMethods(wifiRelated.sort());
    log('info', `Found ${allMethods.length} methods on window.fully (${wifiRelated.length} WiFi-related)`);

    // ── Step 2: standard tests ──
    const tests: DiagItem[] = [
      { label: t('pair2_diag_fully_exists'), fn: () => String(typeof window.fully !== 'undefined'), status: 'pending', value: '' },
      { label: 'getFullyVersion()', fn: () => { try { return window.fully?.getFullyVersion() || 'N/A'; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'getFullyVersionCode()', fn: () => { try { const f = window.fully as unknown as Record<string, (...args: unknown[]) => unknown>; return String(f?.getFullyVersionCode?.()) || 'N/A'; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'isWifiEnabled()', fn: () => { try { return String(window.fully?.isWifiEnabled()); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'isWifiConnected()', fn: () => { try { return String(window.fully?.isWifiConnected()); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'getWifiSsid()', fn: () => { try { return window.fully?.getWifiSsid() || t('pair2_diag_empty'); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'getIp4Address()', fn: () => { try { return window.fully?.getIp4Address() || t('pair2_diag_empty'); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'typeof openWifiSettings', fn: () => { try { return typeof window.fully?.openWifiSettings; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'typeof enableWifi', fn: () => { try { return typeof window.fully?.enableWifi; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: `typeof scanWifi (${t('pair2_diag_removed')})`, fn: () => { try { return typeof (window.fully as unknown as Record<string, unknown>)?.scanWifi; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: `typeof connectWifiSsid (${t('pair2_diag_removed')})`, fn: () => { try { return typeof (window.fully as unknown as Record<string, unknown>)?.connectWifiSsid; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      // Try alternative names
      { label: 'typeof connectWifi', fn: () => { try { return typeof (window.fully as unknown as Record<string, unknown>)?.connectWifi; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'typeof scanWifiNetworks', fn: () => { try { return typeof (window.fully as unknown as Record<string, unknown>)?.scanWifiNetworks; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'typeof getWifiNetworks', fn: () => { try { return typeof (window.fully as unknown as Record<string, unknown>)?.getWifiNetworks; } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      // Check settings
      { label: 'setting: enableWifi', fn: () => { try { return String(window.fully?.getBooleanSetting('enableWifi')); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'setting: enableWifiApi', fn: () => { try { return String(window.fully?.getBooleanSetting('enableWifiApi')); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'setting: wifiApi', fn: () => { try { return String(window.fully?.getBooleanSetting('wifiApi')); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: 'strSetting: licenseType', fn: () => { try { return window.fully?.getStringSetting('licenseType') || t('pair2_diag_empty'); } catch (e) { return `ERR: ${e}`; } }, status: 'pending', value: '' },
      { label: t('pair2_diag_total_found'), fn: () => t('pair2_diag_total_value', { total: allMethods.length, wifi: wifiRelated.length }), status: 'pending', value: '' },
    ];
    setItems([...tests]);

    for (let i = 0; i < tests.length; i++) {
      await wait(80);
      const val = tests[i].fn();
      const isOk = !val.startsWith('ERR') && val !== 'undefined' && val !== 'N/A' && val !== 'false';
      tests[i] = { ...tests[i], value: val, status: isOk ? 'ok' : 'fail' };
      log(isOk ? 'ok' : 'err', `DIAG: ${tests[i].label} → ${val}`);
      setItems([...tests]);
    }
    setRunning(false);
  };

  const dS = {
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto' as const },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 4, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' },
    logBox: { background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 10, maxHeight: 200, overflowY: 'auto' as const, fontFamily: 'Courier New, monospace', fontSize: 11, lineHeight: 1.6 },
  };

  return (
    <div style={dS.overlay} onClick={onClose}>
      <div style={dS.modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🔧 {t('pair_diag_title')}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer' }}>✕</button>
        </div>

        <button
          style={{ ...S.btn, marginBottom: 16, opacity: running ? 0.5 : 1 }}
          onClick={runDiag}
          disabled={running}
        >
          {running ? <><span style={spinnerSmStyle} /> {t('pair_diag_running')}</> : `▶ ${t('pair_diag_run')}`}
        </button>

        {items.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {items.map((it, i) => (
              <div key={i} style={dS.row}>
                <span style={{ fontSize: 13, color: '#f1f5f9' }}>{it.label}</span>
                <span style={{
                  fontSize: 12, fontFamily: 'Courier New, monospace', fontWeight: 600,
                  color: it.status === 'ok' ? '#10B981' : it.status === 'fail' ? '#EF4444' : '#64748b',
                }}>
                  {it.status === 'pending' ? '⏳' : it.status === 'ok' ? '✅' : '❌'} {it.value}
                </span>
              </div>
            ))}
          </div>
        )}

        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px', color: '#94a3b8' }}>📋 {t('pair_diag_log', { n: logEntries.length })}</h3>

        {/* WiFi-related methods found */}
        {wifiMethods.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px', color: '#06B6D4' }}>📶 {t('pair_diag_wifi_methods', { n: wifiMethods.length })}</h3>
            <div style={{ background: '#0f172a', border: '1px solid rgba(6,182,212,0.3)', borderRadius: 8, padding: 10, fontSize: 12, fontFamily: 'Courier New, monospace', color: '#06B6D4' }}>
              {wifiMethods.map((m, i) => <div key={i}>{m}</div>)}
            </div>
          </div>
        )}

        {/* ALL methods on window.fully */}
        {fullyMethods.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px', color: '#A78BFA' }}>📦 {t('pair_diag_all_methods', { n: fullyMethods.length })}</h3>
            <div style={{ background: '#0f172a', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 8, padding: 10, fontSize: 11, fontFamily: 'Courier New, monospace', color: '#A78BFA', maxHeight: 200, overflowY: 'auto' as const }}>
              {fullyMethods.map((m, i) => <div key={i}>{m}</div>)}
            </div>
          </div>
        )}

        <div ref={logRef} style={dS.logBox}>
          {logEntries.length === 0 ? (
            <span style={{ color: '#64748b' }}>{t('pair_diag_no_logs')}</span>
          ) : logEntries.map((e, i) => {
            const c = e.level === 'err' ? '#EF4444' : e.level === 'warn' ? '#F59E0B' : e.level === 'ok' ? '#10B981' : '#94a3b8';
            const time = new Date(e.ts).toLocaleTimeString(locale);
            return <div key={i} style={{ color: c }}>[{time}] {e.msg}</div>;
          })}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────
const WifiProvisioningPage = () => {
  const { t, tp } = useI18n();
  useEffect(injectKeyframes, []);

  const [step, setStep] = useState<Step>(1);
  const [result, setResult] = useState<Result>(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [errRetry, setErrRetry] = useState<Step | null>(null);
  const [hotspotSsid, setHotspotSsid] = useState('Prise-Setup');
  const [networks, setNetworks] = useState<WifiNetwork[]>([]);
  const [selNet, setSelNet] = useState<WifiNetwork | null>(null);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [subStep, setSubStep] = useState<SubStep>('sending');
  const [connSsid, setConnSsid] = useState<string | null>(null);
  const [connIp, setConnIp] = useState<string | null>(null);
  const [mqttOk, setMqttOk] = useState(false);
  const [mock, setMock] = useState(() => localStorage.getItem('wifi_mock') === 'true');

  // Fully Kiosk specific state
  const [fullyMode, setFullyMode] = useState(false);
  const [fullyConnecting, setFullyConnecting] = useState(false);
  const [showDiag, setShowDiag] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollToRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mt = useRef(true);
  useEffect(() => { mt.current = true; return () => { mt.current = false; sp(); }; }, []);

  // e-WattelZCE native app package name
  const [appPackage, setAppPackage] = useState('com.zcegroup.appairage');
  const [appLaunchError, setAppLaunchError] = useState<string | null>(null);

  // Launch e-WattelZCE native Android app via Fully Kiosk
  const launchNativeApp = () => {
    if (!window.fully) return;
    setAppLaunchError(null);
    try {
      log('info', `Launching app: ${appPackage}`);
      window.fully.startApplication(appPackage);
    } catch (e) {
      const msg = t('pair_app_launch_error', { error: String(e) });
      log('err', msg);
      setAppLaunchError(msg);
    }
  };

  // Detect Fully Kiosk on mount
  useEffect(() => {
    const detected = isFullyKiosk();
    setFullyMode(detected);
    log(detected ? 'ok' : 'info', detected ? '📱 Fully Kiosk Browser detected!' : '🌐 Standard browser mode');
    if (detected) {
      try {
        const ver = window.fully!.getFullyVersion();
        log('info', `Fully version: ${ver}`);
      } catch { /* ignore */ }
      const current = fullyGetCurrentWifi();
      if (current) {
        log('info', `Current WiFi: ${current.ssid} (${current.ip})`);
        if (current.ssid === hotspotSsid) {
          log('ok', 'Already on hotspot, skipping to scan');
          setStep(2);
        }
      }
    }
  }, [hotspotSsid]);

  const sp = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (pollToRef.current) { clearTimeout(pollToRef.current); pollToRef.current = null; }
  }, []);

  const toggleMock = () => {
    const n = !mock; setMock(n);
    n ? localStorage.setItem('wifi_mock', 'true') : localStorage.removeItem('wifi_mock');
    _mC = false;
  };

  const goErr = (msg: string, retry: Step | null) => { setErrMsg(msg); setErrRetry(retry); setResult('error'); setStep(5); log('err', msg); };

  // Step 1 - Fully Kiosk: Open WiFi settings + poll for hotspot connection
  const fullyConnectToHotspot = () => {
    if (!fullyMode) return;
    setFullyConnecting(true); setErrMsg(null);
    log('info', `Opening WiFi settings — please connect to "${hotspotSsid}"`);
    fullyOpenWifiSettings();

    // Poll getWifiSsid() every 2s to detect when user connects to hotspot
    let elapsed = 0;
    const maxWait = 120000; // 2 minutes max
    sp(); // clear any previous polling
    pollRef.current = setInterval(async () => {
      elapsed += 2000;
      const current = fullyGetCurrentWifi();
      const currentSsid = current?.ssid || '';
      log('info', `Polling WiFi... SSID: "${currentSsid}" (${Math.round(elapsed / 1000)}s)`);

      if (currentSsid === hotspotSsid) {
        sp();
        log('ok', `Connected to hotspot "${hotspotSsid}"!`);
        if (mt.current) { setFullyConnecting(false); setStep(2); }
        return;
      }

      // Also try to reach ESP32 directly (SSID name might differ slightly)
      if (elapsed > 6000 && elapsed % 6000 === 0) {
        try {
          await apiStatus();
          sp();
          log('ok', 'ESP32 reachable! Proceeding to scan.');
          if (mt.current) { setFullyConnecting(false); setStep(2); }
          return;
        } catch { /* not yet connected */ }
      }

      if (elapsed >= maxWait) {
        sp();
        log('err', 'Timeout waiting for hotspot connection');
        if (mt.current) {
          setFullyConnecting(false);
          setErrMsg(t('pair_err_fk_timeout', { ssid: hotspotSsid }));
        }
      }
    }, 2000);
  };

  // Stop polling when user cancels
  const cancelFullyPolling = () => {
    sp();
    setFullyConnecting(false);
    log('info', 'Polling cancelled by user');
  };

  // Step 1 - Browser: just check ESP32 status
  const checkConn = async () => {
    setLoading(true); setErrMsg(null);
    try {
      const st = await apiStatus();
      if (!mt.current) return;
      if (st.connected) { setConnSsid(st.ssid); setConnIp(st.ip); setMqttOk(st.mqtt); setResult('success'); setStep(5); }
      else setStep(2);
    } catch { if (mt.current) setErrMsg(t('pair_err_cannot_reach', { ssid: hotspotSsid })); }
    finally { if (mt.current) setLoading(false); }
  };

  // Step 2 - Scan WiFi networks
  const doScan = async () => {
    setLoading(true); setErrMsg(null);
    log('info', 'Starting WiFi scan via ESP32...');
    try {
      const nets = await apiScan();
      log('ok', `ESP32 scan: ${nets.length} networks`);
      if (mt.current) {
        setNetworks(nets.sort((a, b) => b.rssi - a.rssi));
        log('ok', `Scan done: ${nets.length} networks`);
      }
    } catch (e) {
      if (mt.current) setErrMsg(t('pair_err_scan'));
      log('err', `Scan failed: ${e}`);
    }
    finally { if (mt.current) setLoading(false); }
  };
  useEffect(() => { if (step === 2 && networks.length === 0) { doScan(); } }, [step]);

  // Step 3 → 4 : Configure ESP32 + reconnect tablet
  const submitCfg = async () => {
    if (!selNet) return;
    setLoading(true); setErrMsg(null); setSubStep('sending'); setStep(4); setResult(null);
    log('info', `Configuring borne for ${selNet.ssid}...`);

    try {
      const res = await apiConfigure(selNet.ssid, password);
      if (!mt.current) return;
      if (res.success) {
        setConnIp(res.ip || null); setConnSsid(selNet.ssid); setSubStep('connecting');
        log('ok', `Config sent. IP: ${res.ip}`);

        // Fully Kiosk: open WiFi settings for user to reconnect to home WiFi
        if (fullyMode && !isMock()) {
          log('info', 'Borne configured! Opening WiFi settings to reconnect tablet...');
          await wait(2000);
          setSubStep('verifying');
          fullyOpenWifiSettings();

          // Poll for reconnection to home WiFi (any non-hotspot SSID)
          let reconElapsed = 0;
          pollRef.current = setInterval(() => {
            reconElapsed += 2000;
            const current = fullyGetCurrentWifi();
            const currentSsid = current?.ssid || '';
            log('info', `Reconnect polling... SSID: "${currentSsid}" (${Math.round(reconElapsed / 1000)}s)`);
            if (currentSsid && currentSsid !== hotspotSsid) {
              sp();
              setSubStep('done');
              setConnSsid(current!.ssid);
              setConnIp(current!.ip);
              setMqttOk(true);
              setResult('success');
              setStep(5);
              log('ok', `Reconnected to "${currentSsid}". Done!`);
            } else if (reconElapsed >= 60000) {
              sp();
              setSubStep('done'); setMqttOk(false); setResult('success'); setStep(5);
              log('warn', 'Tablet reconnection timeout, but borne is configured');
            }
          }, 2000);
          return;
        }

        // Browser mode: poll for status
        let elapsed = 0;
        pollRef.current = setInterval(async () => {
          elapsed += 2000;
          try {
            const st = await apiStatus();
            if (!mt.current) return;
            if (st.connected) { setSubStep('verifying'); if (st.mqtt) { sp(); setSubStep('done'); setConnSsid(st.ssid); setConnIp(st.ip); setMqttOk(true); setResult('success'); setStep(5); } }
          } catch { if (elapsed > 10000 && mt.current) { sp(); setSubStep('done'); setMqttOk(false); setResult('success'); setStep(5); } }
        }, 2000);
        pollToRef.current = setTimeout(() => { if (!mt.current) return; sp(); goErr(t('pair_err_conn_expired'), 3); }, 30000);
      } else {
        const msgs: Record<string, string> = { WRONG_PASSWORD: t('pair_err_wrong_pw_short'), NETWORK_NOT_FOUND: t('pair_err_net_not_found_short'), CONNECTION_TIMEOUT: t('pair_err_conn_timeout_short') };
        const retries: Record<string, Step> = { WRONG_PASSWORD: 3, NETWORK_NOT_FOUND: 2, CONNECTION_TIMEOUT: 3 };
        const code = res.error || 'UNKNOWN';
        goErr(msgs[code] || t('pair_err_unknown_short'), retries[code] || 1);
      }
    } catch { if (mt.current) { sp(); goErr(t('pair_err_send_short'), 1); } }
    finally { if (mt.current) setLoading(false); }
  };

  const restart = () => { sp(); _mC = false; setStep(1); setResult(null); setNetworks([]); setSelNet(null); setPassword(''); setErrMsg(null); setSubStep('sending'); setConnSsid(null); setConnIp(null); setMqttOk(false); };
  const retryFrom = (s: Step) => { sp(); setErrMsg(null); setResult(null); setStep(s); };

  /* ── Render: Stepper ── */
  const Stepper = () => {
    const labels = [t('pair_step_hotspot'), t('pair_step_scan'), t('pair_step_password'), t('pair_step_connection'), t('pair_step_result')];
    return (
      <div style={S.stepper}>
        {labels.map((l, i) => {
          const n = (i + 1) as Step;
          let cs = { ...S.stepCircle };
          if (n < step) cs = { ...cs, ...S.stepDone };
          else if (n === step && result === 'error') cs = { ...cs, ...S.stepError };
          else if (n === step) cs = { ...cs, ...S.stepActive };
          return (
            <div key={n} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={cs}>{n < step ? '✓' : (n === 5 && result === 'error' ? '✗' : String(n))}</div>
                <span style={{ fontSize: 10, color: n === step ? '#f1f5f9' : '#64748b' }}>{l}</span>
              </div>
              {n < 5 && <div style={n < step ? { ...S.stepLine, ...S.stepLineActive } : S.stepLine} />}
            </div>
          );
        })}
      </div>
    );
  };

  /* ── Helpers ── */
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

  const openBorne = () => {
    window.location.href = `http://192.168.4.1`;
  };

  /* ── Render: Step 1 ── */
  const Step1 = () => (
    <div style={S.card}>
      <h2 style={S.cardTitle}>📡 {t('pair_s1_title')}</h2>
      <p style={S.cardSub}>
        {fullyMode
          ? t('pair_s1_sub_fully')
          : t('pair_s1_sub_browser')}
      </p>

      {/* Fully Kiosk: Launch Native App - PROMINENT */}
      {fullyMode && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(6,182,212,0.15), rgba(16,185,129,0.15))',
          border: '2px solid rgba(6,182,212,0.4)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 24,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 8, marginTop: 0 }}>
            {t('pair_app_title')}
          </h3>
          <p style={{ fontSize: 14, color: '#94a3b8', margin: '0 0 16px', lineHeight: 1.5 }}>
            {t('pair_app_desc')}
          </p>
          <button
            onClick={launchNativeApp}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, padding: '16px 36px',
              background: 'linear-gradient(135deg, #06B6D4, #10B981)',
              color: '#fff', fontSize: 17, fontWeight: 700, border: 'none',
              borderRadius: 12, cursor: 'pointer', width: '100%',
              boxShadow: '0 4px 24px rgba(6,182,212,0.3)',
            }}
          >
            🚀 {t('pair_app_open')}
          </button>
          {appLaunchError && (
            <div style={{ ...alertStyle('rgba(239,68,68,0.15)', '#EF4444'), marginTop: 12, textAlign: 'left', fontSize: 13 }}>
              ⚠️ {appLaunchError}
            </div>
          )}
          <details style={{ marginTop: 16, textAlign: 'left' }}>
            <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>⚙️ {t('pair_app_change_package')}</summary>
            <input
              style={{ ...S.input, marginTop: 8, fontSize: 12 }}
              value={appPackage}
              onChange={e => setAppPackage(e.target.value)}
              placeholder="com.zcegroup.appairage"
            />
          </details>
        </div>
      )}

      {/* Separator for web-based flow */}
      {fullyMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const }}>{t('pair_or_web')}</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
        </div>
      )}

      {/* Mode indicator */}
      {fullyMode && (
        <div style={alertStyle('rgba(16,185,129,0.15)', '#10B981')}>
          📱 <strong>{t('pair_fully_detected')}</strong> — {t('pair_fully_detected_hint')}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 6 }}>{t('pair_hotspot_ssid_label')}</label>
        <input style={S.input} value={hotspotSsid} onChange={e => setHotspotSsid(e.target.value)} placeholder="Prise-Setup" />
      </div>

      {/* Fully Kiosk mode: auto-connect flow */}
      {fullyMode && (
        <>
          <div style={S.instrItem}><span style={S.instrNum}>1</span><div style={S.instrText}><strong style={{ color: '#f1f5f9' }}>{t('pair_fk_i1_strong')}</strong> {t('pair_fk_i1_rest')}</div></div>
          <div style={S.instrItem}><span style={S.instrNum}>2</span><div style={S.instrText}>{t('pair_fk_i2_before')} <strong style={{ color: '#10B981' }}>"{t('pair_btn_open_wifi_settings')}"</strong> {t('pair_fk_i2_after')}</div></div>
          <div style={S.instrItem}><span style={S.instrNum}>3</span><div style={S.instrText}>{t('pair_fk_i3_before')} <span style={S.ssidBadge}>📶 {hotspotSsid}</span> {t('pair_fk_i3_after')}</div></div>
          <div style={S.instrItem}><span style={S.instrNum}>4</span><div style={S.instrText}>{t('pair_fk_i4')}</div></div>

          <div style={alertStyle('rgba(59,130,246,0.1)', '#3B82F6')}>💡 {t('pair_fk_internet_hint')}</div>
          {errMsg && <div style={alertStyle('rgba(239,68,68,0.1)', '#EF4444')}>⚠️ {errMsg}</div>}

          <button
            style={{ ...S.btn, opacity: !hotspotSsid.trim() ? 0.5 : 1, marginBottom: 12 }}
            onClick={fullyConnecting ? cancelFullyPolling : fullyConnectToHotspot}
            disabled={!hotspotSsid.trim()}
          >
            {fullyConnecting ? <>⏹ {t('pair_cancel_detection')}</> : <>📶 {t('pair_btn_open_wifi_settings')}</>}
          </button>

          {/* Polling status indicator */}
          {fullyConnecting && (
            <div style={{ ...alertStyle('rgba(6,182,212,0.1)', '#06B6D4'), display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={spinnerSmStyle} />
              <span>{t('pair_waiting_before')} <strong>"{hotspotSsid}"</strong>... {t('pair_waiting_after')}</span>
            </div>
          )}

          {/* Manual check button */}
          {!fullyConnecting && (
            <button
              style={{ ...S.btnSec, width: '100%' }}
              onClick={async () => {
                const current = fullyGetCurrentWifi();
                if (current?.ssid === hotspotSsid) {
                  log('ok', 'Already on hotspot!');
                  setStep(2);
                } else {
                  try {
                    await apiStatus();
                    log('ok', 'ESP32 reachable!');
                    setStep(2);
                  } catch {
                    setErrMsg(t('pair_err_not_on_hotspot', { ssid: current?.ssid || t('pair_none') }));
                  }
                }
              }}
            >
              🔍 {t('pair_check_manual')}
            </button>
          )}
        </>
      )}

      {/* Browser mode: manual instructions + redirect */}
      {!fullyMode && (
        <>
          <div style={S.instrItem}><span style={S.instrNum}>1</span><div style={S.instrText}><strong style={{ color: '#f1f5f9' }}>{t('pair_br_i1_strong')}</strong> {t('pair_br_i1_rest')}</div></div>
          <div style={S.instrItem}><span style={S.instrNum}>2</span><div style={S.instrText}>{t('pair_br_i2')}<br /><span style={S.ssidBadge}>📶 {hotspotSsid}</span></div></div>
          <div style={S.instrItem}><span style={S.instrNum}>3</span><div style={S.instrText}>{t('pair_click_on')} <strong style={{ color: '#f1f5f9' }}>"{t('pair_btn_open_charger_ui')}"</strong> {t('pair_br_i3_middle')} <strong style={{ color: '#06B6D4' }}>192.168.4.1</strong>.</div></div>

          {isHttps && (
            <div style={alertStyle('rgba(245,158,11,0.1)', '#F59E0B')}>
              ⚠️ <strong>{t('pair_https_strong')}</strong> {t('pair_https_rest')}<br />
              {t('pair_https_redirect_before')} <strong>http://192.168.4.1</strong> {t('pair_https_redirect_after')}
            </div>
          )}
          <div style={alertStyle('rgba(59,130,246,0.1)', '#3B82F6')}>💡 {t('pair_br_internet_hint')}</div>
          {errMsg && <div style={alertStyle('rgba(239,68,68,0.1)', '#EF4444')}>⚠️ {errMsg}</div>}

          <button
            style={{ ...S.btn, opacity: !hotspotSsid.trim() ? 0.5 : 1, marginBottom: 12 }}
            onClick={openBorne}
            disabled={!hotspotSsid.trim()}
          >
            🌐 {t('pair_btn_open_charger_ui')} (192.168.4.1)
          </button>

          {(!isHttps || mock) && (
            <button
              style={{ ...S.btnSec, width: '100%', opacity: loading || !hotspotSsid.trim() ? 0.5 : 1 }}
              onClick={checkConn}
              disabled={loading || !hotspotSsid.trim()}
            >
              {loading ? <><span style={spinnerSmStyle} /> {t('pair_checking')}</> : <>🔍 {t('pair_check_embedded')}</>}
            </button>
          )}
        </>
      )}
    </div>
  );

  /* ── Render: Step 2 ── */
  const Step2 = () => (
    <div style={S.card}>
      <h2 style={S.cardTitle}>📡 {t('pair_s2_title')}</h2>
      <p style={S.cardSub}>{t('pair_s2_sub')}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 14, color: '#94a3b8' }}>{tp('pair_networks', networks.length)}</span>
        <button style={{ ...S.btnSec, padding: '8px 14px', fontSize: 13 }} onClick={doScan} disabled={loading}>
          {loading ? <><span style={spinnerSmStyle} /> {t('pair_scanning_short')}</> : <>🔄 {t('pair_refresh')}</>}
        </button>
      </div>
      {errMsg && <div style={alertStyle('rgba(245,158,11,0.1)', '#F59E0B')}>⚠️ {errMsg}</div>}
      {loading && networks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}><div style={spinnerStyle} /><span style={{ color: '#94a3b8' }}>{t('pair_scan_in_progress')}</span></div>
      ) : networks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32 }}><p style={{ fontSize: 48 }}>📡</p><p style={{ color: '#94a3b8' }}>{t('pair_no_network')}</p><button style={{ ...S.btn, width: 'auto', marginTop: 12 }} onClick={doScan}>🔄 {t('pair_retry')}</button></div>
      ) : networks.map((n, i) => (
        <div key={`${n.ssid}-${i}`} style={S.netItem} onClick={() => { setSelNet(n); setPassword(''); setStep(3); }}>
          <span style={{ fontSize: 16, letterSpacing: 2 }}>{signalBars(n.rssi)}</span>
          <div style={{ flex: 1 }}><div style={S.netSsid}>{n.ssid}</div><div style={S.netMeta}>{t(signalLabelKey(n.rssi))} •{n.rssi} dBm{n.encryption !== 'OPEN' ? ` • 🔒 ${n.encryption}` : ''}</div></div>
          <span style={{ color: '#64748b', fontSize: 20 }}>›</span>
        </div>
      ))}
    </div>
  );

  /* ── Render: Step 3 ── */
  const Step3 = () => {
    if (!selNet) return null;
    const isOpen = selNet.encryption === 'OPEN';
    const valid = isOpen || password.length >= 8;
    return (
      <div style={S.card}>
        <h2 style={S.cardTitle}>🔑 {t('pair_s3_title')}</h2>
        <p style={S.cardSub}>{isOpen ? t('pair_s3_open') : t('pair_s3_enter')}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', marginBottom: 20 }}>
          <span style={{ fontSize: 16 }}>{signalBars(selNet.rssi)}</span>
          <div><div style={{ fontWeight: 600 }}>{selNet.ssid}</div><div style={{ fontSize: 12, color: '#64748b' }}>{selNet.encryption} • {selNet.rssi} dBm</div></div>
        </div>
        {!isOpen && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{t('pair_password_label')}</label>
            <div style={{ position: 'relative' }}>
              <input style={S.input} type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={t('pair_password_placeholder')} autoFocus />
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? t('pair_hide_password') : t('pair_show_password')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 }}>{showPw ? '🙈' : '👁️'}</button>
            </div>
            <div style={{ fontSize: 12, color: password.length > 0 && password.length < 8 ? '#EF4444' : '#64748b', marginTop: 6 }}>{password.length > 0 && password.length < 8 ? t('pair_pw_min_required') : t('pair_pw_min')}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button style={S.btnSec} onClick={() => { setStep(2); setPassword(''); }}>← {t('pair_change_network')}</button>
          <button style={{ ...S.btn, flex: 1, opacity: !valid ? 0.5 : 1 }} onClick={submitCfg} disabled={!valid || loading}>
            {loading ? <><span style={spinnerSmStyle} /> {t('pair_configuring')}</> : <>⚡ {t('pair_configure_charger')}</>}
          </button>
        </div>
      </div>
    );
  };

  /* ── Render: Step 4 ── */
  const Step4 = () => {
    const subs: { key: SubStep; label: string; icon: string }[] = [
      { key: 'sending', label: t('pair_sub_sending'), icon: '📤' },
      { key: 'connecting', label: t('pair_sub_connecting'), icon: '📡' },
      { key: 'verifying', label: t('pair_sub_verifying'), icon: '☁️' },
      { key: 'done', label: t('pair_sub_done'), icon: '✅' },
    ];
    const order: SubStep[] = ['sending', 'connecting', 'verifying', 'done'];
    const ci = order.indexOf(subStep);
    return (
      <div style={S.card}>
        <h2 style={S.cardTitle}>⚡ {t('pair_s4_title')}</h2>
        <p style={S.cardSub}>{t('pair_s4_wait')}</p>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={spinnerStyle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {subs.map((s, i) => {
              const done = i < ci; const active = i === ci;
              return (<div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: !done && !active ? 0.4 : 1 }}>
                <span style={{ fontSize: 18 }}>{done ? '✓' : s.icon}</span>
                <span style={{ fontSize: 14, color: active ? '#f1f5f9' : done ? '#10B981' : '#64748b', fontWeight: active ? 600 : 400 }}>{s.label}</span>
              </div>);
            })}
          </div>
          {(subStep === 'connecting' || subStep === 'verifying') && (
            <div style={{ ...alertStyle('rgba(245,158,11,0.1)', '#F59E0B'), marginTop: 24, textAlign: 'left' }}>⚠️ <strong>{t('pair_s4_notice_strong')}</strong><br />{t('pair_s4_notice_rest')}</div>
          )}
        </div>
      </div>
    );
  };

  /* ── Render: Step 5 Success ── */
  const SuccessView = () => (
    <div style={{ ...S.card, textAlign: 'center' }}>
      <div style={S.successIcon}>✓</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>{t('pair_success_title')}</h2>
      <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>{t('pair_success_sub_page')}</p>
      <div style={S.infoGrid}>
        <div style={S.infoItem}><div style={S.infoLabel}>{t('pair_info_wifi')}</div><div style={S.infoVal}>📶 {connSsid || '—'}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>{t('pair_info_ip')}</div><div style={S.infoVal}>🌐 {connIp || '—'}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>{t('pair_info_mqtt')}</div><div style={S.infoVal}>{mqttOk ? <span style={{ color: '#10B981' }}>✅ {t('pair_mqtt_connected')}</span> : <span style={{ color: '#F59E0B' }}>⏳ {t('pair_mqtt_waiting')}</span>}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>{t('pair_info_status')}</div><div style={S.infoVal}><span style={{ color: '#10B981' }}>🟢 {t('pair_online')}</span></div></div>
      </div>
      <button style={S.btn} onClick={() => { window.location.href = '/'; }}>✅ {t('pair_back_dashboard')}</button>
    </div>
  );

  /* ── Render: Step 5 Error ── */
  const ErrorView = () => (
    <div style={{ ...S.card, textAlign: 'center' }}>
      <div style={S.errorIcon}>✗</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: '#EF4444' }}>{t('pair_error_title')}</h2>
      <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>{errMsg}</p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        {errRetry && <button style={S.btn} onClick={() => retryFrom(errRetry)}>🔄 {t('pair_retry')}</button>}
        <button style={S.btnSec} onClick={restart}>↩️ {t('pair_restart')}</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>⚡ {t('pair_title')}</h1>
        <div style={S.mockRow}>
          {fullyMode && (
            <span style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, marginRight: 8 }}>📱 FULLY KIOSK</span>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={mock} onChange={toggleMock} style={{ accentColor: '#06B6D4' }} />
            <span>{t('pair_mock_mode')}</span>
          </label>
          {mock && <span style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>🧪 {t('pair2_mock_badge')}</span>}
          <button
            onClick={() => setShowDiag(true)}
            style={{ background: 'rgba(139,92,246,0.15)', color: '#A78BFA', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            🔧 {t('pair_diag_btn')}
          </button>
        </div>
      </div>
      <Stepper />
      <div style={S.content}>
        {step === 1 && <Step1 />}
        {step === 2 && <Step2 />}
        {step === 3 && <Step3 />}
        {step === 4 && <Step4 />}
        {step === 5 && result === 'success' && <SuccessView />}
        {step === 5 && result === 'error' && <ErrorView />}
      </div>
      {showDiag && <DiagnosticPanel onClose={() => setShowDiag(false)} />}
    </div>
  );
};

export { WifiProvisioningPage };
export default WifiProvisioningPage;
